import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { ProjectsRail } from '@/components/workshop/ProjectsRail';
import { profile, topicPath, type Topic, type WorkshopProject } from '@/data/profile';
import { AppLink } from '@/lib/router';

type TopicPageProps = {
  topic: Topic;
  project?: WorkshopProject;
};

export function TopicPage({ topic, project }: TopicPageProps) {
  const index = profile.topics.findIndex((item) => item.slug === topic.slug);
  const previous = index > 0 ? profile.topics[index - 1] : undefined;
  const next = index >= 0 && index < profile.topics.length - 1 ? profile.topics[index + 1] : undefined;
  const projects = topic.projects;
  const tiles = project ? undefined : topic.tiles;
  const heading = project?.label ?? topic.title;
  const hasBody = !project && topic.sections.length > 0;
  const hasLede = !project && Boolean(topic.lede);

  return (
    <Box as="main" id="main">
      <Box
        className={`topic-shell topic-shell--${topic.slug}${projects ? ' topic-shell--projects' : ''}`}
        px={{ base: '5', md: '8' }}
        pt={{ base: '14', md: '20' }}
        pb={{ base: '20', md: '28' }}
      >
        {tiles ? (
          <Box maxW="6xl" mx="auto">
            <p className="kicker">{topic.label}</p>
            <Heading
              as="h1"
              className="topic-heading"
              color="fg"
              fontSize={{ base: '4xl', md: '6xl' }}
              letterSpacing="-0.05em"
              lineHeight="0.92"
              mb="6"
            >
              {heading}
            </Heading>
            <nav className="doors doors--hub" aria-label={topic.label}>
              {tiles.map((tile, tileIndex) => (
                <AppLink key={tile.href} href={tile.href} className="door door--featured" external={tile.external}>
                  <span className="door__idx">{String(tileIndex + 1).padStart(2, '0')}</span>
                  <span className="door__label">{tile.label}</span>
                </AppLink>
              ))}
            </nav>
          </Box>
        ) : (
          <>
            <Flex
              className="topic-shell__main"
              maxW="6xl"
              mx="auto"
              direction={{ base: 'column', md: 'row' }}
              gap={{ base: '10', md: '16' }}
            >
              <Box flex="1" position={{ md: 'sticky' }} top={{ md: '6.5rem' }} alignSelf="flex-start">
                <p className="kicker">{topic.label}</p>
                <Heading
                  as="h1"
                  className="topic-heading"
                  color="fg"
                  fontSize={{ base: '4xl', md: '6xl' }}
                  letterSpacing="-0.05em"
                  lineHeight="0.92"
                  mb="6"
                >
                  {heading}
                </Heading>
                {hasLede ? <p className="lede">{topic.lede}</p> : null}
              </Box>
              <Box flex="1">
                {hasBody ? (
                  <Box className="glass" p={{ base: '6', md: '8' }}>
                    <div className="prose">
                      {topic.sections.map((section) => (
                        <Text key={section.body} as="p" color="fg" fontSize="lg" lineHeight="1.7" mb="5">
                          {section.body}
                        </Text>
                      ))}
                    </div>
                    {topic.links ? (
                      <Flex gap="2" wrap="wrap" mt="6">
                        {topic.links.map((link) => (
                          <AppLink key={link.href} href={link.href} className="btn" external={link.external}>
                            {link.label}
                          </AppLink>
                        ))}
                      </Flex>
                    ) : null}
                    <nav className="pager" aria-label="Sections">
                      {previous ? (
                        <AppLink href={topicPath(previous.slug)} className="btn">
                          ← {previous.label}
                        </AppLink>
                      ) : (
                        <AppLink href="/" className="btn">
                          ← Home
                        </AppLink>
                      )}
                      {next ? (
                        <AppLink href={topicPath(next.slug)} className="btn">
                          {next.label} →
                        </AppLink>
                      ) : (
                        <AppLink href="/" className="btn">
                          Home →
                        </AppLink>
                      )}
                    </nav>
                  </Box>
                ) : null}
              </Box>
            </Flex>
            {projects ? <ProjectsRail topic={topic} projects={projects} /> : null}
          </>
        )}
      </Box>
    </Box>
  );
}
